import { RefObject, useEffect, useRef, useState } from 'react'

interface UseDynamicLimitTagsParams {
  enabled: boolean
  limitTags: number
  value: any
  deps?: ReadonlyArray<unknown>
}

interface UseDynamicLimitTagsResult {
  containerRef: RefObject<HTMLDivElement>
  chipMeasureRef: RefObject<HTMLDivElement>
  moreMeasureRef: RefObject<HTMLSpanElement>
  dynamicLimitTags: number
}

// Horizontal gap kept between the last visible chip and the overflow indicator.
const OVERFLOW_SAFETY_GAP = 8

const parsePx = (value: string | null) => Number.parseFloat(value || '0') || 0

const measureHorizontalMargin = (element: HTMLElement) => {
  const style = window.getComputedStyle(element)
  return parsePx(style.marginLeft) + parsePx(style.marginRight)
}

/**
 * Computes how many selected chips fit on a single row of a MUI Autocomplete,
 * so the collapsed view can show "chip chip chip +N" tailored to the field width.
 *
 * Measurement is done against a hidden, off-screen copy of the chips (referenced
 * by `chipMeasureRef`) plus a hidden "+N" indicator (`moreMeasureRef`), avoiding
 * any layout flash on the visible control.
 */
const useDynamicLimitTags = ({
  enabled,
  limitTags,
  value,
  deps = [],
}: UseDynamicLimitTagsParams): UseDynamicLimitTagsResult => {
  const containerRef = useRef<HTMLDivElement>(null)
  const chipMeasureRef = useRef<HTMLDivElement>(null)
  const moreMeasureRef = useRef<HTMLSpanElement>(null)
  const [dynamicLimitTags, setDynamicLimitTags] = useState(limitTags)

  useEffect(() => {
    if (!enabled) {
      setDynamicLimitTags(limitTags)
      return
    }
    if (typeof ResizeObserver === 'undefined') return

    const container = containerRef.current
    const chipMeasureContainer = chipMeasureRef.current
    const moreMeasure = moreMeasureRef.current
    if (!container || !chipMeasureContainer || !moreMeasure) return

    const getAvailableWidth = (inputRoot: HTMLElement) => {
      const rootStyle = window.getComputedStyle(inputRoot)
      const horizontalPadding = parsePx(rootStyle.paddingLeft) + parsePx(rootStyle.paddingRight)
      const endAdornment = inputRoot.querySelector<HTMLElement>('.MuiAutocomplete-endAdornment')
      const adornmentWidth = endAdornment?.getBoundingClientRect().width ?? 0
      return Math.max(
        0,
        inputRoot.clientWidth - adornmentWidth - horizontalPadding - OVERFLOW_SAFETY_GAP,
      )
    }

    const getChipWidths = () => {
      const chipElements = Array.from(
        chipMeasureContainer.querySelectorAll<HTMLElement>('.MuiAutocomplete-tag'),
      )
      return chipElements.map(
        (chip) => chip.getBoundingClientRect().width + measureHorizontalMargin(chip),
      )
    }

    const getOverflowWidth = (remaining: number) => {
      moreMeasure.textContent = `+${remaining}`
      return moreMeasure.getBoundingClientRect().width + measureHorizontalMargin(moreMeasure)
    }

    const calculateLimit = () => {
      const inputRoot = container.querySelector<HTMLElement>('.MuiAutocomplete-inputRoot')
      if (!inputRoot) return

      const availableWidth = getAvailableWidth(inputRoot)
      const chipWidths = getChipWidths()

      let visibleCount = 0
      let usedWidth = 0

      for (let index = 0; index < chipWidths.length; index += 1) {
        const nextWidth = usedWidth + chipWidths[index]
        const remaining = chipWidths.length - index - 1
        const overflowWidth = remaining > 0 ? getOverflowWidth(remaining) : 0

        if (nextWidth + overflowWidth > availableWidth) break

        usedWidth = nextWidth
        visibleCount += 1
      }

      const nextLimit = Math.max(1, visibleCount)
      setDynamicLimitTags((current) => (current === nextLimit ? current : nextLimit))
    }

    calculateLimit()

    const inputRoot = container.querySelector<HTMLElement>('.MuiAutocomplete-inputRoot')
    const observer = new ResizeObserver(calculateLimit)
    if (inputRoot) observer.observe(inputRoot)
    observer.observe(container)

    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, limitTags, value, ...deps])

  return { containerRef, chipMeasureRef, moreMeasureRef, dynamicLimitTags }
}

export default useDynamicLimitTags
